/* Progressive enhancement: all product and comparison links work without JavaScript. */
(() => {
  const displayColor = color => ({ 'Burgundy Color':'Burgundy', Sagegreen:'Sage Green' }[color] || color);
  const media = window.OnaInstantMedia;
  // Temporary: colors change only on click or explicit keyboard selection. Set true to restore color previews.
  const colorHoverPreviews = false;
  const finePointer = event => colorHoverPreviews && event.pointerType !== 'touch' && matchMedia('(hover:hover) and (pointer:fine)').matches;
  const setPressed = (controls, active) => controls.forEach(control => control.setAttribute('aria-pressed', String(control === active)));
  const updateStudy = (root, shade, color) => {
    const study = root.querySelector('[data-ona-study]');
    if (study) {
      study.style.setProperty('--ona-study-shade', shade);
      study.querySelector('svg')?.setAttribute('aria-label', `${color} illustrated curtain style study; not a measured product photograph`);
    }
  };
  class OnaAtelierCard extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.swatches = [...this.querySelectorAll('[data-oa-swatch]')];
      this.stopWarm = media.watch(this, () => this.swatches.map(swatch => swatch.dataset.room));
      this.addEventListener('pointerover', event => {
        const swatch = event.target.closest('[data-oa-swatch]');
        if (swatch && finePointer(event)) this.select(swatch);
      }, { signal });
      this.addEventListener('focusin', event => {
        const swatch = event.target.closest('[data-oa-swatch]'); if (colorHoverPreviews && swatch) this.select(swatch);
      }, { signal });
      this.addEventListener('click', event => {
        const swatch = event.target.closest('[data-oa-swatch]');
        if (swatch && this.contains(swatch)) {
          event.preventDefault(); this.select(swatch);
        }
        if (event.target.closest('[data-oa-detail-toggle]')) {
          const enabled = this.dataset.detail !== 'true';
          this.dataset.detail = String(enabled);
          const button = this.querySelector('[data-oa-detail-toggle]');
          button?.setAttribute('aria-pressed', String(enabled));
          const label = button?.querySelector('[data-oa-detail-label]');
          if (label) label.textContent = enabled ? 'Room view' : 'Fabric detail';
        }
      }, { signal });
      this.addEventListener('keydown', event => {
        const swatch = event.target.closest('[data-oa-swatch]');
        if (!swatch || !this.contains(swatch)) return;
        if (event.key === ' ') { event.preventDefault(); swatch.click(); }
        const direction = { ArrowRight:1, ArrowDown:1, ArrowLeft:-1, ArrowUp:-1 }[event.key];
        if (!direction && event.key !== 'Home' && event.key !== 'End') return;
        event.preventDefault();
        const index = this.swatches.indexOf(swatch);
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? this.swatches.length - 1 : (index + direction + this.swatches.length) % this.swatches.length;
        this.swatches[next].focus(); this.select(this.swatches[next]);
      }, { signal });
    }
    select(swatch) {
      if (this.requestedSwatch === swatch) return;
      this.requestedSwatch = swatch;
      const color = displayColor(swatch.dataset.color);
      const token = this.imageToken = (this.imageToken || 0) + 1;
      const study = this.querySelector('[data-oa-card-study]');
      const detailButton = this.querySelector('[data-oa-detail-toggle]');
      const commit = () => {
        setPressed(this.swatches, swatch);
        this.classList.toggle('oa-card--dream-home', swatch.dataset.dreamHero === 'true');
        const label = this.querySelector('[data-oa-color-label]'); if (label) label.textContent = color;
        this.querySelectorAll('[data-oa-card-link]').forEach(link => { link.href = swatch.href; });
        const price = this.querySelector('[data-oa-card-price]');
        if (price && swatch.dataset.price) {
          price.textContent = swatch.dataset.price;
          const was = this.querySelector('[data-oa-card-was]'), compare = was?.querySelector('[data-oa-card-compare]');
          if (compare) { compare.textContent = swatch.dataset.compare || ''; was.hidden = !swatch.dataset.compare; }
        }
        updateStudy(this, swatch.dataset.shade, color);
        this.dataset.detail = 'false'; this.dataset.displayedColor = swatch.dataset.color;
        detailButton?.setAttribute('aria-pressed', 'false');
        const detailLabel = detailButton?.querySelector('[data-oa-detail-label]'); if (detailLabel) detailLabel.textContent = 'Fabric detail';
        const oldDetail = this.querySelector('[data-oa-detail]'); if (oldDetail) oldDetail.hidden = true;
        if (detailButton) detailButton.hidden = true;
      };
      if (!swatch.dataset.room) {
        commit(); if (study) study.hidden = false;
        const oldRoom = this.querySelector('[data-oa-room]'); if (oldRoom) oldRoom.hidden = true;
        return;
      }
      const room = this.querySelector('[data-oa-room]');
      media.prepare(swatch.dataset.room, this, room).then(image => {
        if (!this.isConnected || this.imageToken !== token) return;
        commit();
        image.setAttribute('data-oa-room', '');
        media.mount(room, image, `${color} curtain room styling visualization`);
        if (!room) this.querySelector('.oa-card__picture').prepend(image);
        if (study) study.hidden = true;
        if (!swatch.dataset.detail) return;
        const detail = this.querySelector('[data-oa-detail]');
        media.prepare(swatch.dataset.detail, this, detail).then(next => {
          if (!this.isConnected || this.imageToken !== token) return;
          next.classList.add('oa-card__detail'); next.setAttribute('data-oa-detail', '');
          media.mount(detail, next, `${color} curtain fabric detail styling visualization`);
          if (!detail) this.querySelector('.oa-card__picture').append(next);
          if (detailButton) detailButton.hidden = false;
        }).catch(() => {});
      }).catch(() => { if (this.imageToken === token) this.requestedSwatch = null; });
    }
    disconnectedCallback() { this.imageToken = (this.imageToken || 0) + 1; this.stopWarm?.(); this.requestedSwatch = null; this.controller?.abort(); this.controller = null; }
  }
  class OnaAtelierAssortment extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController(); const { signal } = this.controller;
      this.buttons = [...this.querySelectorAll('[data-oa-filter]')];
      this.cards = [...this.querySelectorAll('ona-atelier-card')];
      this.buttons.forEach(button => { const value = button.dataset.oaFilter; if (value === 'all' || value === 'textured') return; if (!this.cards.some(card => card.dataset.oaTexture === value || card.dataset.oaKind === value || card.dataset.oaColorFamily === value)) button.hidden = true; });
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-oa-filter]');
        if (button && this.contains(button)) this.apply(button.dataset.oaFilter, true);
      }, { signal });
      window.addEventListener('popstate', () => this.fromURL(), { signal });
      this.fromURL();
    }
    fromURL() { this.apply(new URL(location.href).searchParams.get('texture') || 'all', false); }
    apply(texture, write) {
      const selected = this.buttons.find(button => button.dataset.oaFilter === texture && (!button.hidden || button.dataset.oaFilter === 'textured')) || this.buttons[0];
      texture = selected.dataset.oaFilter; setPressed(this.buttons, selected);
      const texturedButton = this.buttons.find(button => button.dataset.oaFilter === 'textured');
      if (texturedButton) texturedButton.hidden = texture !== 'textured';
      let count = 0;
      this.cards.forEach(card => { card.hidden = texture !== 'all' && card.dataset.oaTexture !== texture && card.dataset.oaKind !== texture && card.dataset.oaColorFamily !== texture; if (!card.hidden) count++; });
      this.querySelector('[data-oa-result-count]').textContent = `${count} ${count === 1 ? 'color' : 'colors'}`;
      if (write) {
        const url = new URL(location.href);
        if (texture === 'all') url.searchParams.delete('texture'); else url.searchParams.set('texture', texture);
        history.pushState({}, '', url.pathname + url.search + url.hash);
      }
    }
    disconnectedCallback() { this.controller?.abort(); this.controller = null; }
  }
  class OnaAtelierStyle extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController(); const { signal } = this.controller;
      this.colors = [...this.querySelectorAll('[data-oa-style-color]')];
      try { this.mediaRows = JSON.parse(this.querySelector('[data-oa-style-media]')?.textContent || '{}'); } catch { this.mediaRows = {}; }
      this.galleryButtons = [...this.querySelectorAll('[data-oa-gallery-view]')];
      this.photoDialog = this.querySelector('[data-oa-photo-dialog]');
      const photoFrame = this.querySelector('.oa-style__photo-frame');
      if (photoFrame) this.stopWarm = media.watch(photoFrame, () => this.colors.map(control => this.rowFor(control)?.room));
      this.addEventListener('pointerover', event => {
        const control = event.target.closest('[data-oa-style-color]');
        if (control && finePointer(event)) this.select(control, false);
      }, { signal });
      const restore = event => {
        if (!colorHoverPreviews) return;
        const palette = event.target.closest('.oa-palette');
        if (palette && !palette.contains(event.relatedTarget)) this.select(this.committedControl, false);
      };
      this.addEventListener('pointerout', event => { if (finePointer(event)) restore(event); }, { signal });
      this.addEventListener('focusin', event => { const control = event.target.closest('[data-oa-style-color]'); if (colorHoverPreviews && control) this.select(control, false); }, { signal });
      this.addEventListener('focusout', restore, { signal });
      this.addEventListener('click', event => {
        const control = event.target.closest('[data-oa-style-color]');
        if (control && this.contains(control)) this.select(control, true);
        const galleryButton = event.target.closest('[data-oa-gallery-view]');
        if (galleryButton && this.contains(galleryButton)) this.showPhoto(galleryButton);
        if (event.target.closest('[data-oa-gallery-zoom]')) this.enlarge();
        if (event.target.closest('[data-oa-gallery-close]')) this.photoDialog?.close();
      }, { signal });
      this.photoDialog?.addEventListener('close', () => this.photoOpener?.focus(), { signal });
      this.photoDialog?.addEventListener('click', event => {
        if (event.target !== this.photoDialog) return;
        const rect = this.photoDialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.photoDialog.close();
      }, { signal });
      this.addEventListener('keydown', event => {
        const control = event.target.closest('[data-oa-style-color]');
        if (!control || !this.contains(control)) return;
        const direction = { ArrowRight:1, ArrowDown:1, ArrowLeft:-1, ArrowUp:-1 }[event.key];
        if (!direction && event.key !== 'Home' && event.key !== 'End') return;
        event.preventDefault();
        const index = this.colors.indexOf(control);
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? this.colors.length - 1 : (index + direction + this.colors.length) % this.colors.length;
        this.colors[next].focus(); this.select(this.colors[next], true);
      }, { signal });
      window.addEventListener('popstate', () => this.fromURL(), { signal }); this.fromURL();
    }
    fromURL() {
      const color = new URL(location.href).searchParams.get('color');
      this.committedControl = this.colors.find(control => control.dataset.color === color) || this.colors[0];
      this.select(this.committedControl, false);
    }
    rowFor(control) { return this.mediaRows.colors?.[control?.dataset.color] || (this.mediaRows.color === control?.dataset.color ? this.mediaRows : null); }
    select(control, write) {
      if (!control) return;
      if (write) {
        this.committedControl = control;
        const url = new URL(location.href);
        if (url.searchParams.get('color') !== control.dataset.color) {
          url.searchParams.set('color', control.dataset.color); history.pushState({}, '', url.pathname + url.search + url.hash);
        }
      }
      if (this.requestedControl === control) return;
      this.requestedControl = control;
      const previous = this.galleryButtons.find(button => button.getAttribute('aria-pressed') === 'true');
      const row = this.rowFor(control);
      const button = previous && row?.[previous.dataset.oaGalleryView] ? previous : this.galleryButtons.find(item => item.dataset.oaGalleryView === 'room');
      this.renderPhoto(control, button);
    }
    renderPhoto(control, button) {
      const photoStage = this.querySelector('[data-oa-photo-stage]');
      const study = this.querySelector('[data-oa-study-stage]');
      const thumbnails = this.querySelector('[data-oa-gallery-thumbnails]');
      const rawColor = control.dataset.color;
      const row = this.rowFor(control), view = button?.dataset.oaGalleryView || 'room';
      const token = this.photoToken = (this.photoToken || 0) + 1;
      const frame = this.querySelector('.oa-style__photo-frame');
      const caption = this.querySelector('[data-oa-gallery-caption]');
      const commit = () => {
        setPressed(this.colors, control); this.currentControl = control;
        this.dispatchEvent(new CustomEvent('ona:style-color', { bubbles:true, detail:{ color:rawColor, kind:this.dataset.oaKind } }));
        this.querySelector('[data-oa-color-label]').textContent = displayColor(rawColor);
        updateStudy(this, control.dataset.shade, displayColor(rawColor));
      };
      if (!row?.[view] || !photoStage) {
        commit(); if (photoStage) photoStage.hidden = true; if (study) study.hidden = false; if (thumbnails) thumbnails.hidden = true;
        if (caption) caption.textContent = `${displayColor(rawColor)} · Illustrated color study`;
        return;
      }
      const current = this.querySelector(`[data-oa-gallery-image][data-view="${view}"]`);
      media.prepare(row[view], frame, current).then(image => {
        if (!this.isConnected || this.photoToken !== token) return;
        commit();
        image.setAttribute('data-oa-gallery-image', ''); image.dataset.view = view;
        media.mount(current, image, `${displayColor(rawColor)} curtain ${button?.getAttribute('aria-label') || view}, styling visualization`);
        if (!current) photoStage.prepend(image);
        this.querySelectorAll('[data-oa-gallery-image]').forEach(item => { item.hidden = item !== image; });
        this.galleryButtons.forEach(button => {
          const source = row[button.dataset.oaGalleryView];
          button.hidden = !source;
          const thumbnail = button.querySelector('img');
          if (thumbnail && source) { const url = new URL(source, location.origin); url.searchParams.set('width', '180'); thumbnail.src = url.href; }
        });
        setPressed(this.galleryButtons, button); frame.dataset.view = view;
        photoStage.dataset.color = rawColor; photoStage.hidden = false;
        if (study) study.hidden = true; if (thumbnails) thumbnails.hidden = false;
        if (caption) caption.textContent = `${displayColor(rawColor)} · ${button?.getAttribute('aria-label') || 'Room view'} · Styling visualization`;
        media.warm([row.detail, row.wide], frame);
      }).catch(() => {
        if (this.photoToken !== token) return;
        this.requestedControl = null;
        if (caption) caption.textContent = `Couldn’t load ${displayColor(rawColor)}. The current photograph remains displayed.`;
      });
    }
    showPhoto(button) {
      if (button) this.renderPhoto(this.requestedControl || this.currentControl || this.committedControl, button);
    }
    enlarge() {
      const image = this.querySelector('[data-oa-gallery-image]:not([hidden])');
      if (!image || !this.photoDialog || this.querySelector('[data-oa-photo-stage]')?.hidden) return;
      const enlarged = this.photoDialog.querySelector('[data-oa-enlarged]');
      const url = new URL(image.src); url.searchParams.set('width', '2400');
      enlarged.src = url.href; enlarged.alt = image.alt;
      this.photoOpener = this.querySelector('[data-oa-gallery-zoom]');
      this.photoDialog.showModal();
    }
    disconnectedCallback() { this.photoToken = (this.photoToken || 0) + 1; this.stopWarm?.(); this.requestedControl = null; if (this.photoDialog?.open) this.photoDialog.close(); this.controller?.abort(); this.controller = null; }
  }
  class OnaFabricStory extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      try { this.data = JSON.parse(this.querySelector('[data-oa-story-data]').textContent); } catch { return; }
      this.controller = new AbortController(); const { signal } = this.controller;
      this.image = this.querySelector('[data-oa-story-image]');
      this.caption = this.querySelector('[data-oa-story-caption]');
      this.stopWarm = media.watch(this, () => Object.values(this.data.colors || {}), this.image);
      const kind = this.dataset.oaStoryKind;
      if (kind === 'grommet' || this.hasAttribute('data-oa-native-story')) {
        const productInfo = document.querySelector('product-info.ona-source-product');
        const section = productInfo?.dataset.originalSection || productInfo?.dataset.section;
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, event => {
          if (event.data?.sectionId === section && event.data.variant) this.select(this.data.variants?.[String(event.data.variant.id)]);
        });
        this.select(this.dataset.oaStoryColor);
      } else {
        document.addEventListener('ona:style-color', event => {
          if (event.detail?.kind === kind) this.select(event.detail.color);
        }, { signal });
        const requested = new URL(location.href).searchParams.get('color');
        this.select(this.data.colors?.[requested] ? requested : this.dataset.oaStoryColor);
      }
    }
    select(color) {
      const source = this.data.colors?.[color];
      if (!source || !this.image || color === this.selected) return;
      this.selected = color;
      const label = displayColor(color);
      const kindLabel = { grommet:'textured grommet', backtab:'textured back-tab', velvet:'velvet' }[this.dataset.oaStoryKind];
      const alt = `${label} ${kindLabel} fabric detail styling visualization`;
      const caption = `${label} · Fabric detail styling visualization`;
      const token = this.imageToken = (this.imageToken || 0) + 1;
      this.setAttribute('aria-busy', 'true');
      media.prepare(source, this.image, this.image).then(image => {
        if (!this.isConnected || this.imageToken !== token) return;
        this.image = media.mount(this.image, image, alt);
        this.caption.textContent = caption; this.removeAttribute('aria-busy');
      }).catch(() => {
        if (this.imageToken !== token) return;
        this.selected = null; this.removeAttribute('aria-busy');
      });
    }
    disconnectedCallback() { this.imageToken = (this.imageToken || 0) + 1; this.stopWarm?.(); this.unsubscribe?.(); this.controller?.abort(); this.controller = null; this.selected = null; }
  }
  for (const [name, definition] of [['ona-atelier-card', OnaAtelierCard], ['ona-atelier-assortment', OnaAtelierAssortment], ['ona-atelier-style', OnaAtelierStyle], ['ona-fabric-story', OnaFabricStory]]) {
    if (!customElements.get(name)) customElements.define(name, definition);
  }
})();
